import { animate, style, transition, trigger } from '@angular/animations';
import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, inject, OnInit, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { FilterType, TypeFilterComponent } from '@components/type-filter/type-filter.component';
import { Payment } from '@models/account.model';
import { ErrorResponse } from '@models/responses.model';
import { feedbackTypes } from '@modules/shared/dialogs/feedback-dialog/feedback-dialog.component';
import { TranslateService } from '@ngx-translate/core';
import { AccountService } from '@services/account.service';
import { AlertService } from '@services/alert.service';
import { ConfigLocaleService } from '@services/configLocale.service';
import { DisplayService } from '@services/display.service';
import { FeedbackService } from '@services/feedback.service';
import { TagManagerService } from '@services/tag-manager.service';
import { downloadSummaryOfPayments } from '@utilities/pdf-utils';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
@Component({
  selector: 'gc-payments',
  templateUrl: './payments.component.html',
  styleUrls: ['./payments.component.scss'],
  animations: [
    trigger('slideInOut', [
      transition(':enter', [
        style({ transform: 'translate(100%, -40px)' }),
        animate('200ms linear', style({ transform: 'translate(0%, -40px)' })),
      ]),
      transition(':leave', [animate('1ms linear', style({ transform: 'translate(100%, -40px)' }))]),
    ]),
    trigger('slideOutIn', [
      transition(':enter', [style({ transform: 'translateX(-100%)' }), animate('200ms linear', style({ transform: 'translateX(0%)' }))]),
      transition(':leave', [animate('1ms linear', style({ transform: 'translateX(-100%)' }))]),
    ]),
  ],
  standalone: false,
})
export class PaymentsComponent implements OnInit, AfterViewInit {
  static iconMapping = {
    paid: 'checkbox-marked-circle',
    unpaid: 'alert-circle',
  };
  _paymentsDataSource: MatTableDataSource<Payment> = new MatTableDataSource();
  payments$: Observable<Payment[]> = this._paymentsDataSource.connect();
  currencyCode$: Observable<string>;

  _payments: Payment[] = [];
  searchControl: FormControl = new FormControl();
  sort: MatSort;
  paginator: MatPaginator;
  @ViewChild('search') searchField: ElementRef;
  @ViewChild('typeFilter') typeFilter: TypeFilterComponent;

  isMobile$: BehaviorSubject<boolean>;
  searchMode = false;
  typeFilters: FilterType[] = [
    {
      title: this.translateService.instant('paid_invoices'),
      type: 'paid',
      icon: PaymentsComponent.iconMapping.paid,
      selected: false,
    },
    {
      title: this.translateService.instant('unpaid_invoices'),
      type: 'unpaid',
      icon: PaymentsComponent.iconMapping.unpaid,
      selected: true,
    },
  ];
  selectedPayment: Payment;
  step = 0;

  private readonly tagManagerService = inject(TagManagerService);
  constructor(
    public account: AccountService,
    private readonly configService: ConfigLocaleService,
    private readonly ref: ChangeDetectorRef,
    public display: DisplayService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    public alert: AlertService,
    private readonly feedback: FeedbackService,
    private readonly translateService: TranslateService,
  ) {}

  ngAfterViewInit() {
    const currencyCode = this.configService.$currencyCode();
    this.currencyCode$ = of(currencyCode);
  }
  @ViewChild(MatSort) set sortContent(sortContent: MatSort) {
    if (sortContent) {
      this.sort = sortContent;
      this._paymentsDataSource.sort = this.sort;
      this.ref.detectChanges();
    }
  }
  @ViewChild(MatPaginator) set pageContent(pageContent: MatPaginator) {
    if (pageContent) {
      this.paginator = pageContent;
      this._paymentsDataSource.paginator = this.paginator;
    }
  }

  setStep(index: number) {
    this.step = index;
  }

  nextStep() {
    this.step++;
    if (this.step === this._payments.length) {
      this.step = 0;
    }
  }

  prevStep() {
    this.step--;
    if (this.step < 0) {
      this.step = this._payments.length - 1;
    }
  }

  ngOnInit(): void {
    this.isMobile$ = this.display.isMobile$;
    this._paymentsDataSource.filterPredicate = (data, filter) =>
      ['type', 'accountNumber', 'treatingDoctor'].some((f) => data[f].toLowerCase().indexOf(filter) !== -1);

    combineLatest([this.account.getPayments$(), this.route.queryParams])
      .pipe(
        switchMap(
          ([payments, params]: [Payment[], Params]) => {
            // Set Payments
            this._payments = payments;
            this._paymentsDataSource.data = payments;
            if (params.type?.toLowerCase() === 'success') {
              this.alert
                .messageDialog$(
                  this.translateService.instant('payment_confirmation'),
                  this.translateService.instant('payment_thank_you_message'),
                  this.translateService.instant('confirm'),
                )
                .toPromise()
                .then(() => {
                  this.feedback.feedbackDialog(feedbackTypes.payment)?.subscribe();
                });
            } else if (params.type?.toLowerCase() === 'cancel') {
              this.alert.messageDialog$(
                this.translateService.instant('payment_cancelled'),
                this.translateService.instant('your_payment_was_cancelled_please_try_again'),
                this.translateService.instant('error'),
              );
            } else if (params.type?.toLowerCase() === 'fail') {
              this.alert.messageDialog$(
                this.translateService.instant('payment_failed'),
                this.translateService.instant('your_payment_failed_please_try_again_later'),
                this.translateService.instant('error'),
              );
            } else if (params.hash) {
              this.selectedPayment = payments.find((x) => x.hash === params.hash);
            }
            return this.isMobile$;
          },
          (err: unknown) => {
            if ((err as ErrorResponse).error === 'payments/missing-fields') {
              this.alert.messageDialog$(
                this.translateService.instant('error'),
                this.translateService.instant('cant_retrieve_payments_no_id_number_on_profile'),
                this.translateService.instant('error'),
              );
            }
          },
        ),
      )
      .subscribe();

    this.searchControl.valueChanges.subscribe((searchTerm) => {
      this._paymentsDataSource.filter = searchTerm.trim().toLowerCase();
      this._paymentsDataSource.paginator.firstPage();
    });
  }

  onFilter(data: Payment[]) {
    this._paymentsDataSource.data = data;
  }

  selectPayment(payment: Payment, event: Event) {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        type: null,
        hash: payment.hash,
      },
      // preserve the existing query params in the route
      queryParamsHandling: 'merge',
      // do not trigger navigation
      skipLocationChange: true,
    });
    const gtmTag = {
      event: 'flow-tracking',
      page: this.router.url,
      type: (event.target as HTMLButtonElement).innerText,
      stage: 'payment',
    };
    this.tagManagerService.pushTag(gtmTag);
  }

  onDownload(payment: Payment) {
    downloadSummaryOfPayments(payment, this.translateService);
  }

  backPayment() {
    this.selectedPayment = null;
  }
}
